<template>
    <div class="space-y-6">
        <UFormGroup name="name" label="Name" required>
            <UInput :model-value="modelValue.name" @update:model-value="patch({ name: $event })" type="text" size="xl" placeholder="e.g., Premium Members" />
        </UFormGroup>

        <UFormGroup name="description" label="Description">
            <UTextarea :model-value="modelValue.description" @update:model-value="patch({ description: $event })" autoresize size="xl" placeholder="Describe who this segment targets..." />
        </UFormGroup>

        <div class="grid grid-cols-2 gap-3">
            <button
                v-for="opt in modeOptions"
                :key="opt.value"
                type="button"
                class="flex items-start gap-3 rounded-lg border p-4 text-left transition"
                :class="modelValue.mode === opt.value ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
                @click="patch({ mode: opt.value })"
            >
                <UIcon :name="opt.icon" class="mt-0.5 size-5 shrink-0" :class="modelValue.mode === opt.value ? 'text-primary-600' : 'text-gray-400'" />
                <div>
                    <p class="font-medium text-gray-800">{{ opt.label }}</p>
                    <p class="text-xs text-gray-500">{{ opt.description }}</p>
                </div>
            </button>
        </div>

        <div v-if="modelValue.mode === 'filtered'" class="rounded-lg border border-dashed border-gray-300 p-4">
            <FilterBar :filters="modelValue.chips" @update:filters="patch({ chips: $event })" />
        </div>

        <div v-else>
            <UFormGroup label="Customers">
                <UInputMenu
                    v-model="memberSearch"
                    :search="searchCustomers"
                    placeholder="Search customers to add..."
                    option-attribute="name"
                    nullable
                    size="lg"
                    @update:model-value="onPickMember"
                />
            </UFormGroup>
            <div v-if="modelValue.includes.length" class="mt-2 flex flex-col gap-1">
                <div
                    v-for="c in modelValue.includes"
                    :key="c.id"
                    class="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2.5 py-1.5"
                >
                    <span class="truncate text-sm text-gray-700" :title="`${c.name} #${c.number}`">{{ c.name }} <span class="text-gray-400">#{{ c.number }}</span></span>
                    <UButton icon="i-ph-x-bold" size="2xs" color="gray" variant="ghost" :padded="false" class="shrink-0 p-0.5" @click="removeMember(c.id)" />
                </div>
            </div>
        </div>

        <UDivider label="Preview" />

        <div class="space-y-3">
            <div class="flex items-center justify-between">
                <p class="text-sm text-gray-500">Estimated recipients</p>
                <UBadge v-if="previewing" color="gray" variant="subtle">
                    <UIcon name="i-ph-circle-notch-bold" class="animate-spin mr-1" />
                    Loading...
                </UBadge>
                <UBadge v-else :color="preview.count > 0 ? 'emerald' : 'orange'" variant="subtle">
                    {{ preview.count.toLocaleString() }} customers
                </UBadge>
            </div>

            <div v-if="previewSamples.length" class="border rounded-lg overflow-hidden overflow-x-auto">
                <table class="w-full text-sm">
                    <thead class="bg-gray-50 border-b">
                        <tr>
                            <th class="px-2 py-2 text-left font-medium text-gray-500">#</th>
                            <th class="px-2 py-2 text-left font-medium text-gray-500">Name</th>
                            <th class="px-2 py-2 text-left font-medium text-gray-500">Partner / Policy</th>
                            <th class="px-2 py-2 text-left font-medium text-gray-500">Age</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="customer in previewSamples" :key="customer.id" class="hover:bg-gray-50">
                            <td class="px-2 py-2 font-mono text-xs text-gray-500">{{ customer.number }}</td>
                            <td class="px-2 py-2">
                                <div>{{ customer.first_name }} {{ customer.last_name || '' }}</div>
                                <div class="text-xs text-gray-400">{{ getCountryName(customer.country ?? undefined) }}</div>
                            </td>
                            <td class="px-2 py-2">
                                <div v-if="customer.partner_name" class="text-xs">{{ customer.partner_name }}</div>
                                <div v-if="customer.policy_holder" class="text-xs text-gray-400">{{ customer.policy_holder }}</div>
                                <span v-if="!customer.partner_name && !customer.policy_holder" class="text-gray-300">–</span>
                            </td>
                            <td class="px-2 py-2">{{ calculateAge(customer.birth_date ?? undefined) }}</td>
                        </tr>
                    </tbody>
                </table>
                <p v-if="preview.count > previewSamples.length" class="bg-gray-50 px-2 py-1.5 text-center text-xs text-gray-400">
                    Showing first {{ previewSamples.length }} of {{ preview.count.toLocaleString() }}
                </p>
            </div>
            <p v-else-if="!previewing" class="text-sm text-gray-400 text-center py-4">
                {{ modelValue.mode === 'manual' ? 'Add customers to see them here' : 'Add filters to see matching customers' }}
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { watchDebounced } from '@vueuse/core'
import type { Filter } from '~/types/filter'
import { chipsToCustomerFilter } from '~/utils/customer-filter-chips'
import type { SegmentPreview } from '~/types/segment'
import { getCountryName } from '~/utils/countries'

interface PickedCustomer { id: string; number: number; name: string }
interface Model { name: string; description: string; mode: 'filtered' | 'manual'; chips: Filter[]; includes: PickedCustomer[]; excludes: PickedCustomer[] }

const props = defineProps<{ modelValue: Model }>()
const emit = defineEmits<{ 'update:modelValue': [Model] }>()
const client = useSupabaseClient()

const modeOptions = [
    { value: 'filtered' as const, label: 'Filtered', description: 'Target customers by criteria', icon: 'i-ph-funnel-duotone' },
    { value: 'manual' as const, label: 'Manual', description: 'Pick specific customers', icon: 'i-ph-list-checks-duotone' },
]

function patch(p: Partial<Model>) { emit('update:modelValue', { ...props.modelValue, ...p }) }

const preview = ref<SegmentPreview>({ count: 0, samples: [] })
const previewing = ref(false)
const previewSamples = computed(() => preview.value.samples.slice(0, 10))

async function fetchPreview() {
    previewing.value = true
    try {
        preview.value = await $fetch<SegmentPreview>('/api/segments/preview', {
            method: 'POST',
            body: {
                filter: props.modelValue.mode === 'filtered' ? chipsToCustomerFilter(props.modelValue.chips) : {},
                includes: props.modelValue.mode === 'manual' ? props.modelValue.includes.map(c => c.id) : [],
                excludes: [],
            },
        })
    } finally { previewing.value = false }
}

watchDebounced(() => props.modelValue, fetchPreview, { debounce: 500, deep: true, immediate: true })

async function searchCustomers(q: string): Promise<PickedCustomer[]> {
    if (!q) return []
    const searchQuery = q.trim()
    const parsed = parseInt(searchQuery)
    const isNumeric = !isNaN(parsed)
    const isValidId = isNumeric && parsed <= 2147483647

    const { data } = await client
        .from('customers')
        .select('id, number, first_name, last_name')
        .or([
            `fts.plfts(simple).'${searchQuery}'`,
            `email.ilike.%${searchQuery}%`,
            isNumeric ? `phone.ilike.%${parsed}` : undefined,
            isValidId ? `number.eq.${parsed}` : undefined,
        ].filter(Boolean).join(','))
        .limit(10)

    return (data ?? []).map((c: any) => ({
        id: c.id,
        number: c.number,
        name: [c.first_name, c.last_name].filter(Boolean).join(' '),
    }))
}

const memberSearch = ref<any>(null)

function onPickMember(c: PickedCustomer | null) {
    if (!c) return
    if (!props.modelValue.includes.some(x => x.id === c.id)) {
        patch({ includes: [...props.modelValue.includes, c] })
    }
    nextTick(() => { memberSearch.value = null })
}

function removeMember(id: string) {
    patch({ includes: props.modelValue.includes.filter(x => x.id !== id) })
}

function calculateAge(birthDate?: string): string {
    if (!birthDate) return '–'
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
    return age.toString()
}
</script>
